import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, Person as PersonIcon } from '@mui/icons-material';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  background: '#F8FDF8',
  text: '#2C3E50'
};

const UserDataForm = ({ initialData, onComplete }) => {
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    postalCode: initialData?.postalCode || '',
    emergencyContact: initialData?.emergencyContact || '',
    emergencyPhone: initialData?.emergencyPhone || '',
    occupation: initialData?.occupation || '',
    relationship: initialData?.relationship || 'padre', // padre, madre, tutor
    ...initialData
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    const requiredFields = ['firstName', 'lastName', 'phone', 'address'];

    for (const field of requiredFields) {
      if (!formData[field]?.trim()) {
        setError(`El campo ${getFieldLabel(field)} es obligatorio`);
        return false;
      }
    }

    // Validar teléfono
    const phoneRegex = /^[0-9\s\-\+\(\)]{9,}$/;
    if (!phoneRegex.test(formData.phone)) {
      setError('El formato del teléfono no es válido');
      return false;
    }

    return true;
  };

  const getFieldLabel = (field) => {
    const labels = {
      firstName: 'Nombre',
      lastName: 'Apellidos',
      phone: 'Teléfono',
      address: 'Dirección'
    };
    return labels[field] || field;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const user = auth.currentUser;
      const userRef = doc(db, 'users', user.uid);

      const userData = {
        ...formData,
        email: user.email,
        uid: user.uid,
        updatedAt: new Date(),
        profileCompleted: true
      };

      // Si es la primera vez, añadir campos de creación
      if (!initialData?.uid) {
        userData.createdAt = new Date();
        userData.active = true;
        userData.role = userData.role || 'user';
      }

      if (initialData?.uid) {
        await updateDoc(userRef, userData);
      } else {
        await setDoc(userRef, userData);
      }

      onComplete(userData);

    } catch (error) {
      console.error('Error saving user data:', error);
      setError('Error al guardar los datos. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Paper elevation={0} sx={{ p: 3, backgroundColor: COLORS.background, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PersonIcon sx={{ color: COLORS.accent, mr: 1, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text }}>
            Datos Personales
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Datos básicos */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              name="firstName"
              label="Nombre"
              value={formData.firstName}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              name="lastName"
              label="Apellidos"
              value={formData.lastName}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              name="phone"
              label="Teléfono"
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
              placeholder="Ej: 666 123 456"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Relación con el/los menor/es</InputLabel>
              <Select
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                disabled={loading}
                label="Relación con el/los menor/es"
              >
                <MenuItem value="padre">Padre</MenuItem>
                <MenuItem value="madre">Madre</MenuItem>
                <MenuItem value="tutor">Tutor/a</MenuItem>
                <MenuItem value="abuelo">Abuelo/a</MenuItem>
                <MenuItem value="otro">Otro</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Dirección */}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              name="address"
              label="Dirección"
              value={formData.address}
              onChange={handleChange}
              disabled={loading}
              placeholder="Calle, número, piso..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="city"
              label="Ciudad"
              value={formData.city}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="postalCode"
              label="Código Postal"
              value={formData.postalCode}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          {/* Contacto de emergencia */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: COLORS.text, mb: 2, fontWeight: 'bold' }}>
              Contacto de Emergencia (Opcional)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="emergencyContact"
              label="Nombre del contacto de emergencia"
              value={formData.emergencyContact}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="emergencyPhone"
              label="Teléfono de emergencia"
              value={formData.emergencyPhone}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          {/* Información adicional */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="occupation"
              label="Profesión (Opcional)"
              value={formData.occupation}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              bgcolor: COLORS.accent,
              '&:hover': { bgcolor: COLORS.secondary },
              px: 4,
              py: 1.5
            }}
          >
            {loading ? 'Guardando...' : 'Guardar y Continuar'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserDataForm;